import { z } from 'zod';

/**
 * Calificación según demanda: WF02 sólo gasta Gemini cuando la campaña de la empresa necesita
 * stock. Ver `apps/api/src/modules/qualify-budget/` y la decisión 2026-09-28 en docs/decisions.md.
 */

export const qualifyBudgetSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Envíos objetivo por día de envío (L-V). */
  targetPerDay: z.number().int().min(1).max(1000),
  /** Días de envío (L-V) que tiene que cubrir el stock antes de dejar de calificar. */
  bufferDays: z.number().int().min(1).max(20),
});
export type QualifyBudgetSettings = z.infer<typeof qualifyBudgetSettingsSchema>;

export const QUALIFY_BUDGET_DEFAULTS: QualifyBudgetSettings = {
  enabled: false,
  targetPerDay: 40,
  bufferDays: 3,
};

/**
 * - `disabled`: la empresa no tiene la función prendida → WF02 usa su lote de siempre.
 * - `stock_ok`: el stock enviable cubre el colchón → esta corrida no reclama leads.
 * - `in_flight`: falta stock, pero lo que ya se está calificando lo cubre.
 * - `deficit`: falta stock → se pueden reclamar hasta `maxToQualify`.
 * - `no_data`: no se pudo medir (base del stack caída/lenta) → fail-open, lote de siempre.
 */
export const qualifyBudgetReasonSchema = z.enum([
  'disabled',
  'stock_ok',
  'in_flight',
  'deficit',
  'no_data',
]);
export type QualifyBudgetReason = z.infer<typeof qualifyBudgetReasonSchema>;

/** Respuesta de `GET /api/ingest/qualify-budget` (la consulta el nodo de WF02 en cada corrida). */
export const qualifyBudgetResponseSchema = z.object({
  tenantId: z.uuid(),
  tenant: z.string(),
  enabled: z.boolean(),
  /** `false` = esta corrida NO reclama leads (quedan `pending`, sin intento ni error). */
  allowed: z.boolean(),
  reason: qualifyBudgetReasonSchema,
  /** Explicación en lenguaje llano (va al log de n8n y al panel). */
  message: z.string(),
  /** Tope de leads a reclamar en esta corrida; `null` = sin tope (el LIMIT de siempre de WF02). */
  maxToQualify: z.number().int().min(0).nullable(),
  targetPerDay: z.number().int(),
  bufferDays: z.number().int(),
  /** Stock que tiene que haber: envíos por día × días de colchón. */
  targetStock: z.number().int(),
  /** Stock enviable hoy (Instantly sin contactar + pushes aún no sincronizados + cola de WF03). */
  stock: z.number().int().nullable(),
  instantlyStock: z.number().int().nullable(),
  queueStock: z.number().int().nullable(),
  deficit: z.number().int().nullable(),
  /** Aprobados con email por cada lead calificado (últimos 14 días). */
  yieldRate: z.number().nullable(),
  yieldSource: z.enum(['measured', 'default']).nullable(),
  inFlight: z.number().int().nullable(),
  pending: z.number().int().nullable(),
  qualifiedToday: z.number().int().nullable(),
  checkedAt: z.string(),
});
export type QualifyBudgetResponse = z.infer<typeof qualifyBudgetResponseSchema>;

/** Vista del panel (Config y Operaciones) de una empresa. */
export const qualifyBudgetViewSchema = z.object({
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
  settings: qualifyBudgetSettingsSchema,
  /** Última decisión que tomó la plataforma para WF02 (cada ~5 min mientras corre). */
  last: qualifyBudgetResponseSchema.nullable(),
  lastDecidedAt: z.string().nullable(),
  today: z.object({
    day: z.string().nullable(),
    runsSkipped: z.number().int(),
    runsAllowed: z.number().int(),
    runsFailOpen: z.number().int(),
  }),
  updatedAt: z.string().nullable(),
  updatedByEmail: z.string().nullable(),
});
export type QualifyBudgetView = z.infer<typeof qualifyBudgetViewSchema>;

export const qualifyBudgetListSchema = z.object({ items: z.array(qualifyBudgetViewSchema) });
export type QualifyBudgetList = z.infer<typeof qualifyBudgetListSchema>;
